import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Quote from '../components/Quote';
import { SiteContent } from '../content';

const SearchIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
);

const Shop: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  // Shopify Store Settings
  const SHOPIFY_DOMAIN = 'zp61f0-x0.myshopify.com';

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 200px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => observer.observe(el));

    // Force visibility fallback for elements already in view
    const timeoutId = setTimeout(() => {
      elements?.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) el.classList.add('is-visible');
      });
    }, 500);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [filter, search]);

  const { hero, explanation, assets, closing } = SiteContent.shop;

  const filteredAssets = assets.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'All' || item.type === filter;
    return matchesSearch && matchesFilter;
  });

  const categories = [
    { label: 'ALL', value: 'All' },
    { label: 'LETTERS', value: 'Letter' },
    { label: 'NUMBERS', value: 'Number' },
    { label: 'PHRASES', value: 'Phrase' }
  ];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = 'https://images.unsplash.com/photo-1592919016322-50f094b97ef0?auto=format&fit=crop&q=80&w=800'; // Soil fallback
  };

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper min-h-screen overflow-hidden">
      
      {/* SECTION 1: HERO */}
      <section className="pt-32 md:pt-48 pb-16 text-center px-6">
        <div className="max-w-7xl mx-auto">
          <span className="editorial-label">CURATED COLLECTION</span>
          <div className="animate-on-scroll animate-fade-up">
            <h1 className="hero-title !text-rich-black mb-8 leading-[0.9]">{hero.title}</h1>
            <p className="text-[10px] md:text-[12px] font-black uppercase tracking-[0.5em] text-[#A65D3F] mb-12">{hero.description}</p>
            <Quote text={hero.quote} className="mb-10" />
          </div>
        </div>
      </section>

      {/* SECTION 2: EXPLANATION - Editorial Layout */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-7">
              <div className="animate-on-scroll animate-mask rounded-xl overflow-hidden shadow-2xl aspect-[16/9] img-placeholder">
                <img 
                  src="https://images.unsplash.com/photo-1592919016322-50f094b97ef0?auto=format&fit=crop&q=80&w=1600" 
                  alt="Planting seed" 
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              </div>
           </div>
           <div className="lg:col-span-5 lg:pl-12">
              <span className="editorial-label">THE UTILITY</span>
              <h2 className="section-title text-3xl md:text-5xl mb-8">{explanation.title}</h2>
              <p className="text-lg text-black/70 mb-10 leading-relaxed">
                {explanation.text}
              </p>
              <div className="space-y-4">
                 {explanation.receive.map((item, i) => (
                   <div key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                      <span className="font-bold block text-sm mb-1">{item.label}</span>
                      <span className="text-[10px] text-black/40 uppercase tracking-widest leading-none">{item.desc}</span>
                   </div>
                 ))}
              </div>
           </div>
        </div>
      </section>

      {/* SECTION 3: STICKY FILTER */}
      <section className="sticky top-[70px] md:top-[85px] z-50 bg-[#F2E9D9]/95 backdrop-blur-md border-y border-black/5 py-4 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-6 overflow-x-auto w-full md:w-auto no-scrollbar py-1">
                {categories.map(cat => (
                    <button 
                        key={cat.value}
                        onClick={() => setFilter(cat.value)}
                        className={`text-[9px] font-black uppercase tracking-[0.4em] transition-all whitespace-nowrap border-b-2 pb-1 ${filter === cat.value ? 'text-[#A65D3F] border-[#A65D3F]' : 'text-black/20 border-transparent hover:text-black'}`}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>
            <div className="relative w-full md:w-72">
                <input 
                    type="text" 
                    placeholder="FIND FREQUENCY..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full bg-black/5 border-none rounded-sm py-2.5 pl-10 pr-4 text-[10px] font-bold uppercase tracking-widest focus:ring-1 focus:ring-[#A65D3F] outline-none"
                />
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/20">
                    <SearchIcon />
                </div>
            </div>
        </div>
      </section>

      {/* PRODUCT GRID - Strict Uniform Approach */}
      <section className="py-16 px-4 md:px-12 bg-white/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {filteredAssets.map((item, i) => {
            const shopifyDirectUrl = `https://${SHOPIFY_DOMAIN}/cart/${item.variantId}:1?checkout=1`;
            
            return (
              <div key={item.name} className="flex flex-col bg-white border border-black/5 rounded-sm p-4 animate-on-scroll animate-scale-in group shadow-sm hover:shadow-xl transition-all duration-500">
                {/* Image Container with Shimmer Placeholder */}
                <div className="relative aspect-square mb-6 overflow-hidden rounded-sm img-placeholder">
                  <img 
                    src={item.img} 
                    alt={item.name} 
                    className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
                    onError={handleImageError}
                    onLoad={(e) => (e.currentTarget.parentElement?.classList.remove('img-placeholder'))}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                      <span className="text-white text-9xl md:text-[10rem] serif font-black">{item.char}</span>
                  </div>
                  {/* Digital Badge */}
                  <div className="absolute top-3 left-3 bg-[#A65D3F] text-white text-[8px] font-black px-2 py-1 uppercase tracking-widest rounded-sm">
                    {item.type}
                  </div>
                </div>

                {/* Content Area */}
                <div className="flex flex-col flex-grow text-center">
                  <h3 className="font-condensed text-xl font-black text-rich-black tracking-widest mb-1 truncate">
                    {item.name}
                  </h3>
                  <div className="flex items-center justify-center space-x-2 mb-6">
                    <div className="h-px bg-black/10 flex-grow max-w-[20px]"></div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black/40">Frequency Asset</span>
                    <div className="h-px bg-black/10 flex-grow max-w-[20px]"></div>
                  </div>
                  
                  {/* DUAL CHECKOUT BUTTONS - Physically Visible */}
                  <div className="space-y-3 mt-auto">
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1.5 px-1">
                            <span className="text-[8px] font-black uppercase tracking-widest opacity-40">Digital (NFT)</span>
                            <span className="text-[9px] font-black text-[#A65D3F]">0.23 SOL</span>
                        </div>
                        <a 
                          href={item.marketplaceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary w-full"
                        >
                          MINT ARTIFACT
                        </a>
                    </div>

                    <div className="flex flex-col pt-2 border-t border-black/5">
                        <div className="flex justify-between items-center mb-1.5 px-1">
                            <span className="text-[8px] font-black uppercase tracking-widest opacity-40">Physical Art</span>
                            <span className="text-[9px] font-black text-rich-black">$55.00</span>
                        </div>
                        <a 
                          href={shopifyDirectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary w-full"
                        >
                          BUY WOOD PIECE
                        </a>
                        <p className="text-[8px] font-bold uppercase tracking-widest opacity-60 mt-2 italic text-center text-[#A65D3F]">
                           Includes Digital Redemption
                        </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredAssets.length === 0 && (
            <div className="py-40 text-center">
                <p className="serif italic text-2xl text-black/20">No matching frequencies found in the soil.</p>
                <button onClick={() => {setFilter('All'); setSearch('');}} className="mt-8 text-[10px] font-black uppercase tracking-widest border-b border-black/10 pb-1">Show All Assets</button>
            </div>
        )}
      </section>

      {/* SECTION 4: CLOSING */}
      <section className="py-32 md:py-48 bg-rich-black text-center relative overflow-hidden">
        <div className="vertical-text right-12 top-1/2 -translate-y-1/2 !text-white/5">
            PLANT THE SEED — OWN THE ALPHABET
        </div>
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <Quote large text={closing.quote} className="mb-12" />
          <div className="animate-on-scroll animate-scale-in delay-200">
             <Link to="/contact" className="btn-primary !px-12">Message the Movement</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;